import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, LoadingData, NoData, formatDateForApi, useFormatDate } from '@hwm/ui-lib';
import { Fade, Typography, useTheme, Theme, useMediaQuery } from '@material-ui/core';
//
import uPlot from 'uplot';
import UplotReact from 'uplot-react';
import 'uplot/dist/uPlot.min.css';
import { MaintenanceGraphModel, MaintenanceUniqGraphModel } from '../MaintenanceGraphs.models';
import { maintenanceGraphsColors } from '../MaintenanceGraphs.utils';
import { isEmpty } from 'lodash';
import {
  axesDynamicScalesPlugin,
  axesWidthPlugin,
  drawFrameOnGraph,
  midnightLinePlugin,
  tooltipPlugin,
  useGraphStyles,
} from '@features/GraphsView/MainGraph/Graph/Graph.utils';
import { useReactToPrint } from 'react-to-print';
import { PRINT_HEIGHT, PRINT_WIDTH } from '@features/GraphsView/MainGraph/MainGraph';
import { Trans } from '@lingui/macro';
import { ControlCamera, Print } from '@material-ui/icons';
import { GraphControls } from '@features/GraphsView/MainGraph/GraphControls';
import { ACTUATIONS_GRAPH_SCALE_NAME } from '@features/GraphsView/Actuations/Actuations';
import { isNil } from 'lodash';

const LOADING_DELAY = 1200;

interface MaintenanceGraphProps {
  title: string;
  customTitleComponent?: JSX.Element;
  loading: boolean;
  width: number;
  //
  labels: string[];
  gData: MaintenanceGraphModel<MaintenanceUniqGraphModel>[];
  noDataMsg: string;
  //
  actuationsGraph?: boolean;
  //
  /**
   * `dataTestId` - name of a Maintenance Graph (preferably `<name>-<type>-maintenance-graph`)
   * @example
   * ```tsx
   * <MaintenanceGraph {...props} dataTestId="test-battery-maintenance-graph" />
   * or
   * <MaintenanceGraph {...props} dataTestId="test-ecl-maintenance-graph" />
   */
  dataTestId: string;
}

export const MAINTENANCE_GRAPH_CHART_HEIGHT = 292;
export const MAINTENANCE_GRAPH_SCALE_NAME = 'maintenance_scale';

export const MaintenanceGraph: React.FC<MaintenanceGraphProps> = ({
  title,
  customTitleComponent,
  loading,
  width,
  //
  labels,
  gData,
  noDataMsg,
  actuationsGraph,
  //
  dataTestId,
}) => {
  const SCALE_NAME = actuationsGraph ? ACTUATIONS_GRAPH_SCALE_NAME : MAINTENANCE_GRAPH_SCALE_NAME;

  const classes = useGraphStyles();
  const theme = useTheme<Theme>();
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const extraSmallScreen = useMediaQuery(theme.breakpoints.down('xs'));

  const plotRef = useRef<uPlot>(null);

  const { formatDate } = useFormatDate();

  const [showGraph, setShowGraph] = useState(false);
  const [showPanArrows, setShowPanArrows] = useState(true);

  useEffect(() => {
    loading && setShowGraph(false);
    const timer = setTimeout(() => {
      !loading && setShowGraph(true);
    }, LOADING_DELAY);
    return () => clearTimeout(timer);
  }, [loading, setShowGraph]);

  const graphDates = useMemo(() => {
    const dates =
      gData
        ?.flatMap((el) => el.data)
        ?.map((data) => formatDateForApi(data?.date))
        ?.filter((x) => x !== undefined) ?? [];

    const uniqDates = Array.from(new Set(dates)) ?? [];
    const sortedUniqDates = uniqDates?.sort((a, b) => Date.parse(a) - Date.parse(b)) ?? [];

    return sortedUniqDates;
  }, [gData]);

  const graphDataDictionary: Record<string, number>[] = useMemo(() => {
    const dataDictionary = gData?.map(
      (el) => Object.fromEntries(el.data?.map(({ date, value }) => [formatDateForApi(date), value]) ?? []) ?? {}
    );

    return dataDictionary;
  }, [gData]);

  const graphData = useMemo(() => {
    const data: number[][] = graphDates?.length ? graphDataDictionary?.map(() => Array(graphDates?.length ?? 0)) : [];

    data?.forEach((value, i) => {
      graphDates?.forEach((el, j) => {
        value[j] = graphDataDictionary?.[i]?.[el] ?? null;
      });
    });

    return data;
  }, [graphDates, graphDataDictionary]);

  const series = useMemo(
    () =>
      labels?.map((label, i) => {
        const colorsIndex = i % maintenanceGraphsColors?.length;
        const color = maintenanceGraphsColors?.[colorsIndex];

        return {
          label: label,
          scale: SCALE_NAME,
          spanGaps: true,
          stroke: gData?.[i]?.customColor ?? color,
          show: true,
          width: actuationsGraph ? (i === 3 ? 2 : 1.4) : 1,
          class: title?.replaceAll(' ', '_'),
          points: { show: false },
          dash: actuationsGraph && i === 3 ? [8] : null,
        } as uPlot.Series;
      }),
    [title, labels, actuationsGraph, SCALE_NAME, gData]
  );

  const isEmptyData = useMemo(
    () =>
      !graphData || isEmpty(graphData) || graphData.every((values) => isEmpty(values) || values.every((v) => isNil(v))),
    [graphData]
  );

  // PRINT
  const ref = useRef<HTMLDivElement>();
  const [isPrinting, setIsPrinting] = useState(false);

  const handlePrint = useReactToPrint({
    content: () => ref.current,
    onAfterPrint: () => {
      setIsPrinting(false);
    },
    bodyClass: 'pl-2',
  });

  useEffect(() => {
    if (isPrinting) {
      setTimeout(handlePrint, 44);
    }
  }, [isPrinting, handlePrint]);

  return (
    <>
      {!showGraph ? (
        <div className="h-1/3">
          <LoadingData dataTestId={`${dataTestId}-loading`} />
        </div>
      ) : isEmptyData ? (
        <div className="h-1/3" style={{ minHeight: 220 }}>
          {customTitleComponent ?? (
            <Typography variant="body1" style={{ alignSelf: 'flex-end' }} data-testid={`${dataTestId}-title`}>
              {title}
            </Typography>
          )}
          <NoData additionalText={noDataMsg ?? ''} dataTestId={`${dataTestId}-no-data`} />
        </div>
      ) : (
        <div
          ref={ref}
          className="mb-4"
          style={
            isPrinting
              ? {
                  width: PRINT_WIDTH,
                  minWidth: PRINT_WIDTH,
                  pointerEvents: 'none',
                }
              : { height: '100%' }
          }
          data-testid={`${dataTestId}-wrapper`}
        >
          <div className="flex flex-row justify-between pr-2 mb-1">
            {customTitleComponent ?? (
              <Typography variant="body1" style={{ alignSelf: 'flex-end' }} data-testid={`${dataTestId}-title`}>
                {title}
              </Typography>
            )}
            {!isPrinting && (
              <div className="flex flex-row">
                {extraSmallScreen ? (
                  <Button
                    variant="text"
                    style={{
                      height: 28,
                      width: 44,
                      minWidth: 44,
                    }}
                    onClick={() => setShowPanArrows((prev) => !prev)}
                    dataTestId={`${dataTestId}-camera-control-button`}
                  >
                    <ControlCamera />
                  </Button>
                ) : (
                  <GraphControls plotRef={plotRef} dataTestId={`${dataTestId}-controls`} />
                )}
                <Button
                  style={{
                    height: 28,
                    width: 44,
                    minWidth: 44,
                    marginLeft: 8,
                  }}
                  onClick={() => setIsPrinting(true)}
                  disabled={!Boolean(graphData?.length)}
                  dataTestId={`${dataTestId}-print-button`}
                >
                  <Print />
                </Button>
              </div>
            )}
          </div>
          <Fade in={graphData?.length > 0} {...{ timeout: 800 }}>
            <div
              className="relative rounded-lg pb-2 mr-2 z-50"
              style={{ background: '#eeeeee', fontFamily: 'Ubuntu Sans' }}
            >
              {extraSmallScreen && showPanArrows && (
                <div className="absolute top-1 right-1 z-50">
                  <GraphControls plotRef={plotRef} dataTestId={`${dataTestId}-controls`} />
                </div>
              )}
              <UplotReact
                onCreate={(chart) => (plotRef.current = chart)}
                options={{
                  legend: {
                    show: true,
                    live: false,
                    markers: { width: 8 },
                  } as uPlot.Legend,
                  width: isPrinting ? PRINT_WIDTH : width,
                  height: isPrinting
                    ? PRINT_HEIGHT
                    : smallScreen
                      ? MAINTENANCE_GRAPH_CHART_HEIGHT
                      : 1.4 * MAINTENANCE_GRAPH_CHART_HEIGHT,
                  padding: [24, 44, 0, 4],
                  cursor: {
                    drag: {
                      x: true,
                      y: true,
                      dist: 12, // min distance to use zoom (px)
                      uni: 24, // distance to zoom in both directions
                    },
                  } as uPlot.Cursor,

                  axes: [
                    {
                      space: 64,
                      font: '11px Ubuntu Sans',
                      values: (_, splits) => {
                        const parsedDates = splits.map((d) => d * 1000);
                        const datesToDisplay = parsedDates.map((d, i, arr) => {
                          const date = new Date(d);

                          const currDate = { day: date.getDate(), month: date.getMonth() };
                          const prev = new Date(arr[i - 1]);
                          const prevDate = {
                            day: i !== 0 ? prev.getDate() : -1,
                            month: i !== 0 ? prev.getMonth() : -1,
                          };

                          const showFullDate = currDate.day !== prevDate.day || currDate.month !== prevDate.month;

                          // if 1st date to display or current is next day -> display day-month-year
                          return (showFullDate ? `${formatDate(date, 'P')}\n` : '') + formatDate(date, 'HH:mm');
                        });

                        return datesToDisplay;
                      },
                    },
                    {
                      scale: SCALE_NAME,
                      stroke: '#000000',
                      side: 3,
                      grid: { show: true },
                      show: true,
                    },
                  ],

                  series: [{ class: classes.hiddenSeries }, ...series],

                  plugins: [
                    drawFrameOnGraph(),
                    tooltipPlugin(smallScreen, formatDate),
                    midnightLinePlugin(),
                    axesWidthPlugin(),
                    axesDynamicScalesPlugin(),
                  ],
                }}
                data={[graphDates.map((el) => Date.parse(el) / 1000), ...graphData]}
              />
            </div>
          </Fade>
          {graphData?.length > 0 && (
            <Typography variant="caption" data-testid={`${dataTestId}-time-gmt`}>
              <Trans>Display time in GMT</Trans>
            </Typography>
          )}
        </div>
      )}
    </>
  );
};
